import { InventoryRuleStatus } from "@redotech/crdb-enums/inventory/inventory-rule-status";
import { InventoryRuleTrigger } from "@redotech/crdb-enums/inventory/inventory-rule-trigger";
import {
  inventoryRuleIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import {
  inventoryRuleDraftSchema,
  inventoryRuleExistingItemsSchema,
  inventoryRuleSaveResultSchema,
  inventoryRuleSchema,
  inventoryRuleVersionSchema,
} from "./inventory-rule-models";

/** The warehouse's returns center rules, in run order. */
export const listInventoryRules = unaryRpc({
  name: "listInventoryRules" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    trigger: z.enum(InventoryRuleTrigger).optional(),
    /** Unset lists live and draft rules. */
    statuses: z.array(z.enum(InventoryRuleStatus)).min(1).optional(),
  }),
  output: z.object({ rules: z.array(inventoryRuleSchema) }),
});

export const getInventoryRule = unaryRpc({
  name: "getInventoryRule" as const,
  input: z.object({ ruleId: inventoryRuleIdFormat.schema }),
  output: z.object({
    rule: inventoryRuleSchema,
    /** Newest first. */
    versions: z.array(inventoryRuleVersionSchema),
  }),
});

/** Creates a rule at the last position, or adds a version to an existing one. */
export const saveInventoryRule = unaryRpc({
  name: "saveInventoryRule" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    /** Unset creates a rule. */
    ruleId: inventoryRuleIdFormat.schema.optional(),
    rule: inventoryRuleDraftSchema,
    /** Saving a draft never reaches items. */
    status: z.enum([InventoryRuleStatus.Live, InventoryRuleStatus.Draft]),
    /** Defaults to `next_run`. */
    existingItems: inventoryRuleExistingItemsSchema.optional(),
  }),
  output: inventoryRuleSaveResultSchema.extend({ rule: inventoryRuleSchema }),
});

/** Rewrites the run order of the warehouse's live rules for one trigger. */
export const reorderInventoryRules = unaryRpc({
  name: "reorderInventoryRules" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    trigger: z.enum(InventoryRuleTrigger),
    /** Every live rule for the trigger at the warehouse, in the new order. */
    ruleIds: z.array(inventoryRuleIdFormat.schema).min(1),
  }),
  output: z.object({ rules: z.array(inventoryRuleSchema) }),
});

/** A rule that was ever live is disabled, never deleted. */
export const setInventoryRuleStatus = unaryRpc({
  name: "setInventoryRuleStatus" as const,
  input: z.object({
    ruleId: inventoryRuleIdFormat.schema,
    status: z.enum([InventoryRuleStatus.Live, InventoryRuleStatus.Disabled]),
  }),
  output: z.object({ rule: inventoryRuleSchema }),
});

/** Deletes a rule that was never live. */
export const deleteInventoryRule = unaryRpc({
  name: "deleteInventoryRule" as const,
  input: z.object({ ruleId: inventoryRuleIdFormat.schema }),
  output: z.object({}),
});
